import { useShallow } from "zustand/shallow";
import {
  createApiClient,
  createCompanyApiClient,
  createFieldApiClient,
  RefreshManager,
  type CompanyApiClient,
  type FieldApiClient,
} from "@njbsoft/api-client";
import { useCompanyStore } from "@/store";
import { queryClient } from "@/lib/query-client";
import { tearDownFieldworkForLogout } from "@/lib/fieldwork/engine";
import { readPersistedSessionTokens } from "@/lib/persisted-session";

const API_URL = import.meta.env.VITE_API_URL;

/** Tear down the local session and hard-navigate to login. The fieldwork
 *  teardown is AWAITED first — its purge-vs-residue split must settle before
 *  the state clear retires the pinned identity that keys it — and the hard
 *  nav stays LAST: the full reload guarantees no stale state survives an auth
 *  failure. The gate is the PINNED identity, not a live handle: the profile's
 *  persisted database outlives any one tab, so a sign-out must tear it down
 *  even when this session never mounted the tester subtree (the teardown
 *  opens a handle on demand). A session with no pinned tester identity never
 *  opened one either — nothing awaits, so its clear and nav still run in the
 *  caller's own tick. */
export async function clearCompanySession(): Promise<void> {
  const profileId = useCompanyStore.getState().offlineIdentity?.profileId ?? null;
  if (profileId !== null) {
    await tearDownFieldworkForLogout(profileId);
  }
  const state = useCompanyStore.getState();
  state.setActiveCompanyId(null);
  // The persisted persona snapshot and pinned identity outlive a reload by
  // design; a session teardown is the one event that must retire them, or the
  // next account to sign in on this browser resolves against the last one's
  // personas before its own probes land.
  state.setPersonaSnapshot(null);
  state.setOfflineIdentity(null);
  state.clearAuth();
  queryClient.clear();
  window.location.href = "/login";
}

// Centralized refresh manager -- shared by all three clients and the auth
// guard.
//
// The last three options are the cross-tab layer: the manager takes an
// exclusive Web Lock around every refresh and, inside it, adopts a sibling's
// rotation from the persisted store when that pair is stamped LATER than the
// one captured here — because a tab replaying a token its sibling rotated more
// than the server's grace window ago reads as token theft and revokes the whole
// rotation family.
export const refreshManager = new RefreshManager({
  baseUrl: API_URL,
  getRefreshToken: () => useCompanyStore.getState().refreshToken,
  onTokenRefreshed: (accessToken, refreshToken, expiresAt) => {
    const state = useCompanyStore.getState();
    state.setAuth(accessToken, state.user, refreshToken);
    state.setExpiresAt(new Date(expiresAt).getTime());
  },
  onRefreshFailed: (outcome) => {
    // A transport-failed refresh is not a rejected session: the shell's
    // working-offline state is the signal, and queued capture keeps its
    // identity. Only a server refusal ends the session.
    if (outcome === "offline") return;
    void clearCompanySession();
  },
  getPersistedTokens: readPersistedSessionTokens,
  adoptTokens: (tokens) => {
    useCompanyStore.getState().adoptTokens(tokens);
  },
  getTokenStamp: () => useCompanyStore.getState().rotatedAt,
});

// MAIN-spec client. The portal runs THREE: this one, `companyApiClient`
// (/api/v1/company, the company data plane) and `fieldApiClient`
// (/api/v1/field, the tester data plane). No company or tester DATA flows
// through this one -- its legal surface is exactly these ops:
//
//   /auth/*
//   GET  /company-member-invites/mine
//   POST /company-member-invites/{inviteId}/accept
//   GET  /tester-invites/mine
//   POST /tester-invites/{inviteId}/accept
//   POST /tester-invites/accept
//   POST /tester-invites/lookup
//   POST /testing-companies/register
//   POST /utility-access-requests
//
// The invite-family ops are legal for the same reason /auth/* is, and are NOT
// precedent for data ops: the invitee is neither a company member nor yet a
// tester, so no per-stack gate could admit them, and every one of them is a
// claim-scoped personal-context op carrying no org or company data. The
// token-based accept is the email-link path, and its landing page is THIS
// portal's `/accept-tester-invite` -- the utility app is org-spined and can
// route no one who holds no membership.
//
// The last two are the entry gate's own doors, and they occupy that same
// class by construction: a person with no membership and no grant is exactly
// who calls them, so neither the company stack nor the field stack could gate
// them. Both are bearer-only, take no org and no company, and disclose one
// thing between them -- the name of the utility whose secret the caller
// typed.
//
// `lookup` is the pre-auth half of that same invitee-context family: it
// carries no org or company data either, and is the only op in this list that
// runs with NO session at all -- there being no identity yet to be a member
// or tester of, which is exactly why it cannot leak anything scoped to one.
//
// The deliberate name makes `grep -rn "authApiClient\." src/` an audit, not a
// hunt; `src/lib/README.md` carries the standing command block.
export const authApiClient = createApiClient({
  baseUrl: API_URL,
  getToken: () => useCompanyStore.getState().token,
  refreshManager,
  // After the shared 401 replay learned the tri-state, an offline 401 returns
  // untouched and never reaches here. What does: a server-rejected refresh, and
  // the defensive arm where a refresh succeeded but the request clone was gone.
  onUnauthorized: clearCompanySession,
});

/** The portal's ONE sign-out: revoke the refresh token server-side, THEN tear
 *  the local session down. The revocation is best-effort — a failed POST must
 *  never trap someone in a session they asked to leave — but it is not
 *  optional, and holding it here rather than at each affordance is what stops
 *  a new Sign Out button from shipping as a local-only teardown that leaves a
 *  live refresh token behind. That is not hypothetical: the tester terminals
 *  under `components/tester` cannot name `authApiClient` at all (the
 *  tester-stack boundary guard bans the literal there), so before this helper
 *  existed the guard silently downgraded their sign-out. */
export async function signOutCompanySession(): Promise<void> {
  const { refreshToken } = useCompanyStore.getState();
  if (refreshToken) {
    authApiClient.POST("/auth/logout", { body: { refresh_token: refreshToken } }).catch(() => {
      // Signing out regardless -- server-side revocation is best effort.
    });
  }
  await clearCompanySession();
}

// The company data plane: every workspace hook goes through this client.
export const companyApiClient: CompanyApiClient = createCompanyApiClient({
  baseUrl: `${API_URL}/company`,
  getToken: () => useCompanyStore.getState().token,
  refreshManager,
  onUnauthorized: clearCompanySession,
});

// The tester data plane: every tester-surface hook goes through this client.
// Base is `${API_URL}/field`. Field paths carry no /auth/ segment, so every
// field request is refresh-eligible and shares the portal's one RefreshManager.
// It deliberately sends NO X-Client-Platform header: omitting it is what routes
// device trust through the HttpOnly cookie rather than the JSON body.
export const fieldApiClient: FieldApiClient = createFieldApiClient({
  baseUrl: `${API_URL}/field`,
  getToken: () => useCompanyStore.getState().token,
  refreshManager,
  onUnauthorized: clearCompanySession,
});

export function useCompanyApiClient(): CompanyApiClient {
  return companyApiClient;
}

// Auth context for router
export function useAuth() {
  return useCompanyStore(
    useShallow((s) => ({
      token: s.token,
      refreshToken: s.refreshToken,
      expiresAt: s.expiresAt,
      user: s.user,
      isAuthenticated: s.isAuthenticated,
      setAuth: s.setAuth,
      setRefreshToken: s.setRefreshToken,
      setExpiresAt: s.setExpiresAt,
      rotatedAt: s.rotatedAt,
      adoptTokens: s.adoptTokens,
      setEmailVerified: s.setEmailVerified,
      clearAuth: s.clearAuth,
      mfaSessionToken: s.mfaSessionToken,
      mfaPending: s.mfaPending,
      mfaMethods: s.mfaMethods,
      setMfaChallenge: s.setMfaChallenge,
      clearMfaChallenge: s.clearMfaChallenge,
    })),
  );
}
